// @flow
import { endpoints } from "../../utils/c21-api";
import axios from "axios";
import { debounce } from "lodash";

export const validatePostalCode = async (postalCode) => {
    if (!postalCode) return false;

    const mapboxToken = process.env.GATSBY_MAPBOX_TOKEN;
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${postalCode}.json?access_token=${mapboxToken}&country=be&types=postcode&limit=1`;

    try {
        const response = await axios.get(url);
        return response.data.features.length > 0;
    } catch (error) {
        console.error("Error validating postal code:", error);
        return false;
    }
};

export const submitForm = async (
    values,
    locale,
    executeRecaptcha,
    setFormStatus,
    resetForm,
    agencyId,
    updateDisplayedAgencyAfterSubmission,
    scrollToTop,
) => {
    if (!executeRecaptcha) {
        return;
    }

    const isValidPostalCode = await validatePostalCode(values.postalCode);
    if (!isValidPostalCode) {
        throw new Error("Invalid postal code");
    }

    const token = await executeRecaptcha("homepage");

    const payload = {
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        language: locale,
        phoneNumber: values.phoneNumber,
        postalCode: parseInt(values.postalCode, 10),
        propertyType: values.propertyType,
        token: token,
        city: values.city,
        street: values.street,
        streetNumber: values.number,
        ...(values.extraInfo && { additionalInfo: values.extraInfo }),
        ...(agencyId && { agencyId: agencyId }),
    };

    return axios({
        method: "post",
        url: endpoints.estimation,
        headers: {
            "Content-Type": "application/json",
        },
        data: payload,
    })
        .then((response) => {
            setFormStatus("success");
            resetForm();
            if (response.data.data && response.data.data.agencyId) {
                updateDisplayedAgencyAfterSubmission(response.data.data);
            }
            scrollToTop();
        })
        .catch((error) => {
            setFormStatus("error");
            // eslint-disable-next-line no-console
            console.error("Error submitting estimation form:", error);
        });
};

export const fetchSuggestions = debounce(
    async (
        query,
        setSuggestions,
        accessToken,
        sessionToken,
        country,
        locale,
        setdropdownIsOpen,
    ) => {
        if (!query || query.length < 3) {
            setSuggestions([]);
            return;
        }

        try {
            const url = `https://api.mapbox.com/search/searchbox/v1/suggest?q=${encodeURIComponent(
                query,
            )}&access_token=${accessToken}&session_token=${sessionToken}&types=address,street&country=${country}&language=${locale}&limit=10`;

            const response = await fetch(url);
            const data = await response.json();

            const suggestions = data.suggestions.map((suggestion) => {
                const [postalCode, city] = suggestion.place_formatted
                    .split(",")[0]
                    .split(" ");

                return {
                    name: suggestion.name,
                    mapbox_id: suggestion.mapbox_id,
                    postalCode,
                    city,
                    locality: suggestion.context?.locality?.name,
                    fullDetails: {
                        context: suggestion.context,
                        feature_type: suggestion.feature_type,
                        address: suggestion.address,
                        full_address: suggestion.full_address,
                    },
                };
            });

            setSuggestions(suggestions);
            setdropdownIsOpen(suggestions.length > 0);
        } catch (error) {
            setSuggestions([]);
        }
    },
    300,
);

export const createKeyActions = (
    suggestions,
    focusedSuggestionIndex,
    setFocusedSuggestionIndex,
    handleAddressSelect,
    setFieldValue,
    setdropdownIsOpen,
) => ({
    ArrowDown: (event) => {
        event.preventDefault();
        setFocusedSuggestionIndex((prev) =>
            prev < suggestions.length - 1 ? prev + 1 : prev,
        );
    },
    ArrowUp: (event) => {
        event.preventDefault();
        setFocusedSuggestionIndex((prev) => (prev > 0 ? prev - 1 : prev));
    },
    Enter: (event) => {
        event.preventDefault();
        if (focusedSuggestionIndex >= 0) {
            handleAddressSelect(
                setFieldValue,
                suggestions[focusedSuggestionIndex],
            );
            setdropdownIsOpen(false);
            setFocusedSuggestionIndex(-1);
            event.target.focus();
        }
    },
    Escape: (event) => {
        setdropdownIsOpen(false);
        setFocusedSuggestionIndex(-1);
        event.target.focus();
    },
});
